/**
 * mt-lite.jsx — @material-tailwind/react'in NavbarWithMegaMenu'da KULLANILAN bileşenlerinin
 * sade Tailwind/HTML karşılıkları. Amaç: ~665KB eager material-tailwind paketini bundle'dan
 * tamamen çıkarmak. API (props) MT ile aynı tutuldu ki navbar JSX'i neredeyse değişmesin.
 *
 * Kapsam (yalnızca navbar'ın ihtiyacı): Navbar, Collapse, Typography, Button, IconButton,
 * List, ListItem, Menu (controlled + allowHover), MenuHandler, MenuList, MenuItem.
 */
import React, { createContext, useContext, useState, useRef, useEffect, useLayoutEffect } from "react";

const cx = (...a) => a.filter(Boolean).join(" ");

/* --------------------------- mergeCls ----------------------------- */
/**
 * Default utility class'larını tüketici className'i ile ÇAKIŞMAYACAK şekilde birleştir.
 *
 * Sorun: Tailwind production build'i utility'leri KANONİK sırada emit eder (örn. .flex-row
 * her zaman .flex-col'dan ÖNCE; .text-sm her zaman .text-base'den ÖNCE). Aynı elemanda hem
 * default hem override varsa, CSS'te SONRA gelen kazanır — bu da çoğu zaman default'un
 * override'ı ezmesi demek (ör. List default'u flex-col, navbar override'ı flex-row ister
 * ama prod'da flex-col kazanıp menüyü DİKEY yapar). Dev JIT class'ları keşif sırasına göre
 * ürettiğinden sorun yalnızca production'da görünür ("dev'de çalışır, prod'da patlar").
 *
 * Çözüm: Tüketici bir kategoride (display, flex-yönü, font-size, font-weight, rounded,
 * leading, position) class verdiyse, AYNI kategorideki default token'ı tamamen at. Böylece
 * o property için tek kaynak override olur ve CSS sırası önemsizleşir. Prefix'li (lg:, hover:)
 * tüketici class'ları kategoriye sayılmaz; default base davranışı korunur.
 */
function categoryOf(tok) {
  if (/^(block|inline-block|inline|flex|inline-flex|grid|inline-grid|hidden|table|contents)$/.test(tok)) return "display";
  if (/^flex-(row|col)(-reverse)?$/.test(tok)) return "flex-dir";
  if (/^text-(xs|sm|base|lg|xl|[2-9]xl)$/.test(tok)) return "font-size";
  if (/^font-(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)$/.test(tok)) return "font-weight";
  if (/^rounded(-(none|sm|md|lg|xl|2xl|3xl|full))?$/.test(tok)) return "rounded";
  if (/^leading-/.test(tok)) return "leading";
  if (/^(static|fixed|absolute|relative|sticky)$/.test(tok)) return "position";
  return null;
}
function mergeCls(defaults, className = "") {
  const overrideCats = new Set(
    String(className).split(/\s+/).filter(Boolean).map(categoryOf).filter(Boolean)
  );
  const kept = String(defaults)
    .split(/\s+/)
    .filter(Boolean)
    .filter((tok) => {
      const c = categoryOf(tok);
      return !(c && overrideCats.has(c));
    });
  return cx(kept.join(" "), className);
}

/* ----------------------------- Navbar ----------------------------- */
export const Navbar = React.forwardRef(function Navbar(
  { className = "", children, ...rest },
  ref
) {
  return (
    <nav ref={ref} className={mergeCls("relative", className)} {...rest}>
      {children}
    </nav>
  );
});

/* --------------------------- IconButton --------------------------- */
export const IconButton = React.forwardRef(function IconButton(
  // variant/size/color MT API'siyle uyum için kabul edilir ama stil className'den gelir
  { variant, size, color, className = "", children, type = "button", ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      className={mergeCls(
        "inline-grid place-items-center select-none rounded-lg align-middle transition-all",
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

/* ----------------------------- Button ----------------------------- */
export const Button = React.forwardRef(function Button(
  { variant, size = "md", color, fullWidth, ripple, className = "", children, type = "button", ...rest },
  ref
) {
  // MT Button varsayılan görünümü (mt-lite'a geçince kaybolmuştu): uppercase + bold +
  // boyuta göre padding/yazı boyutu -> butonlar dolgun ve MT'deki gibi görünür. Inline
  // style verilen butonlarda (ör. ana Serdivan+) o stiller yine kazanır (specificity);
  // yalnızca uppercase devreye girer. Padding/font'u inline vermeyen dropdown butonları
  // (Nöbetçi Eczaneler, SABSIS...) eski MT görünümüne döner.
  const sizeCls =
    size === "sm"
      ? "px-4 py-2 text-xs"
      : size === "lg"
      ? "px-7 py-3.5 text-sm"
      : "px-6 py-3 text-xs";
  return (
    <button
      ref={ref}
      type={type}
      className={mergeCls(
        cx(
          "inline-flex items-center justify-center select-none rounded-lg font-bold uppercase leading-normal whitespace-nowrap transition-all",
          sizeCls,
          fullWidth && "w-full"
        ),
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

/* --------------------------- Typography --------------------------- */
export const Typography = React.forwardRef(function Typography(
  { as, variant = "paragraph", color, className = "", children, ...rest },
  ref
) {
  const Tag = as || (typeof variant === "string" && /^h[1-6]$/.test(variant) ? variant : "p");
  // MT varsayılan tipografi yaklaşık karşılıkları. mergeCls çakışan default'u attığı için
  // tüketicinin text-sm/text-xs/font-bold'u production'da da kazanır.
  const variantBase =
    variant === "h6"
      ? "text-base font-semibold leading-relaxed"
      : variant === "paragraph"
      ? "text-base font-normal leading-relaxed"
      : "";
  return (
    <Tag ref={ref} className={mergeCls(variantBase, className)} {...rest}>
      {children}
    </Tag>
  );
});

/* ------------------------------ List ------------------------------ */
export const List = React.forwardRef(function List(
  { className = "", children, ...rest },
  ref
) {
  // <ul> yerine <div>: navbar zaten bir <nav> landmark'ı içinde. ul/li semantiği menü
  // tetikleyici <Menu>/<div> çocuklarıyla karıştığında Lighthouse "Listeler yalnızca <li>
  // içermeli" + "<li> bir <ul> içinde olmalı" hatalarını veriyordu (a11y). Hiç ul/li
  // kullanmayınca bu denetimler tamamen geçersizleşir; layout flex class'larıyla aynı kalır.
  // mergeCls: navbar üst menüsü flex-row verince default flex-col ATILIR -> yatay kalır.
  return (
    <div ref={ref} className={mergeCls("flex flex-col gap-1 min-w-[200px]", className)} {...rest}>
      {children}
    </div>
  );
});

export const ListItem = React.forwardRef(function ListItem(
  { className = "", children, ripple, selected, disabled, role, ...rest },
  ref
) {
  // <li> yerine <div> (yukarıdaki List ile aynı a11y gerekçesi). aria-expanded taşıyan
  // öğe bir açılır menü tetikleyicisidir -> geçerli ARIA için role="button" ver (tetikleyici
  // zaten tabindex=0 + metin etikete sahip). Tüketici role verirse o kazanır.
  const isDisclosure = rest["aria-expanded"] !== undefined;
  return (
    <div
      ref={ref}
      role={role || (isDisclosure ? "button" : undefined)}
      className={mergeCls(
        "flex items-center w-full leading-tight outline-none transition-all cursor-pointer",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
});

/* ---------------------------- Collapse ---------------------------- */
// MT Collapse height-auto animasyonu yerine max-height geçişi (içerik 3000px'i geçmez).
export function Collapse({ open, className = "", children, style, ...rest }) {
  return (
    <div
      className={className}
      style={{
        overflow: "hidden",
        maxHeight: open ? 3000 : 0,
        opacity: open ? 1 : 0,
        transition: "max-height 0.35s ease, opacity 0.25s ease",
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

/* ------------------------------ Menu ------------------------------ */
// Controlled (open + handler) + allowHover destekli. Nested için placement="right/left".
const MenuCtx = createContext(null);

export function Menu({
  open: openProp,
  handler,
  allowHover = false,
  placement = "bottom",
  offset,
  dismiss,
  children,
}) {
  const [internal, setInternal] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : internal;
  const setOpen = (v) => {
    if (typeof handler === "function") handler(v);
    if (!controlled) setInternal(v);
  };
  const isHorizontal =
    typeof placement === "string" &&
    (placement.startsWith("right") || placement.startsWith("left"));

  // Hover kapanma gecikmesi: tetikleyici ile (alttaki/yandaki) panel arasında küçük bir
  // boşluk var; mouse o boşluğu geçerken anında kapanırsa kullanıcı panele ulaşamaz/
  // içinde kaydıramaz. ~220ms gecikme + tekrar girişte iptal, MT'nin popper hover
  // davranışının yerini tutar. Panel zaten root'un DOM çocuğu olduğundan üzerine gelince
  // mouseLeave tetiklenmez; gecikme yalnızca boşluk geçişini toleranslar.
  const closeTimer = useRef(null);
  const rootRef = useRef(null);
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  useEffect(() => cancelClose, []);

  const hoverProps = allowHover
    ? {
        onMouseEnter: () => {
          cancelClose();
          setOpen(true);
        },
        onMouseLeave: () => {
          cancelClose();
          closeTimer.current = setTimeout(() => setOpen(false), 220);
        },
      }
    : {};

  return (
    <MenuCtx.Provider value={{ open, setOpen, allowHover, isHorizontal, offset, rootRef }}>
      <div
        ref={rootRef}
        className="mt-lite-menu"
        // Top-level mega-menü: root static -> MenuList absolute, en yakın positioned ata
        // (position:relative olan <nav>) referans alır -> width:100% navbar genişliği olur.
        // Nested (right/left): root inline-block + relative -> tetikleyiciyi sarar, MenuList
        // left:100% tetikleyicinin SAĞ kenarına yapışır (block olsaydı tüm grid hücresi
        // genişliğini alıp alt menü panelin ortasına düşerdi).
        style={{
          position: isHorizontal ? "relative" : "static",
          display: "inline-block",
        }}
        {...hoverProps}
      >
        {children}
      </div>
    </MenuCtx.Provider>
  );
}

export function MenuHandler({ children }) {
  const ctx = useContext(MenuCtx) || {};
  const child = React.Children.only(children);
  return React.cloneElement(child, {
    onClick: (e) => {
      child.props.onClick?.(e);
      if (!ctx.allowHover) ctx.setOpen?.(!ctx.open);
    },
  });
}

export function MenuList({ className = "", style = {}, children, ...rest }) {
  const ctx = useContext(MenuCtx) || {};
  if (!ctx.open) return null;
  // Nested (yan açılan) alt menü: ABSOLUTE konumlu (transform'lu atalardan etkilenmez;
  // fixed bu yüzden ana sayfadaki Swiper bağlamında çalışmıyordu) + JS flip (sağda yer
  // yoksa sola açılır). Eleman DOM'da menünün çocuğu kaldığından hover ilişkisi korunur.
  // Kırpılmaması için ata panelin overflow'u JSX'te kaldırıldı.
  if (ctx.isHorizontal) {
    return (
      <Flyout ctx={ctx} className={className} style={style} {...rest}>
        {children}
      </Flyout>
    );
  }
  // Top-level mega-menü paneli: navbar'ın hemen altından açılır (absolute).
  return (
    <div
      className={className}
      style={{ position: "absolute", top: "100%", left: 0, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}

// Yan açılan alt menü: nested Menu root'una (relative) göre absolute konumlanır.
// Varsayılan sağa; sağda yer yoksa sola çevrilir (flip). useLayoutEffect ile boyanmadan
// önce yön belirlenir -> flicker yok. Absolute olduğu için transform'lu atalar sorun olmaz.
function Flyout({ ctx, className, style = {}, children, ...rest }) {
  const ref = useRef(null);
  const [side, setSide] = useState("right");
  const off = typeof ctx.offset === "number" ? ctx.offset : 6;

  useLayoutEffect(() => {
    const trigger = ctx.rootRef && ctx.rootRef.current;
    const el = ref.current;
    if (!trigger || !el) return;
    const decide = () => {
      const t = trigger.getBoundingClientRect();
      const w = el.offsetWidth || 230;
      // Sağda taşıyorsa ve solda daha çok yer varsa sola aç.
      const overflowsRight = t.right + off + w > window.innerWidth - 8;
      setSide(overflowsRight && t.left - off - w > 8 ? "left" : "right");
    };
    decide();
    window.addEventListener("resize", decide);
    return () => window.removeEventListener("resize", decide);
  }, [ctx.rootRef, off]);

  const posStyle =
    side === "left"
      ? { position: "absolute", right: `calc(100% + ${off}px)`, top: 0 }
      : { position: "absolute", left: `calc(100% + ${off}px)`, top: 0 };

  return (
    <div ref={ref} className={className} style={{ ...style, ...posStyle }} {...rest}>
      {children}
    </div>
  );
}

export function MenuItem({ className = "", children, disabled, ...rest }) {
  return (
    <div
      className={mergeCls(
        "block w-full rounded-md transition-colors hover:bg-black/5",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
